import { useRef } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';

// components
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

import { PdpRespCondensedListedContent } from '../PdpRespCondensedListedContent/PdpRespCondensedListedContent';
import { PdpSharedRecentHistoryTrackerDeprecated } from '../shared/PdpSharedRecentHistoryTracker/PdpSharedRecentHistoryTrackerDeprecated';
import { PdpRespBuyerAssuranceBox } from '../PdpRespBuyerAssuranceBox/PdpRespBuyerAssuranceBox';
import { PdpRespSimilarItemsGrid } from '../PdpRespSimilarItems/PdpRespSimilarItemsGrid';
import { PdpRespSimilarItemsLanding } from '../PdpRespSimilarItems/PdpRespSimilarItemsLanding';
import { PdpRespSellerItemTileCarousel } from '../PdpRespSimilarItems/PdpRespSellerItemTileCarousel/PdpRespSellerItemTileCarousel';
import { PdpRespCondensedCircles } from '../PdpRespCondensedCircles/PdpRespCondensedCircles';
import { PdpRespDealerChatLoader } from '../PdpRespDealerChat/PdpRespDealerChatLoader';
import { PdpSharedTestimonial } from '../PdpSharedTestimonial/PdpSharedTestimonial';
import { youMayAlsoLike, similarItems } from '../../shared/sharedMessages';
import { SMALL_MEDIUM_TILE_TYPE } from '../../shared/SharedRespRelatedItem/SharedRespRelatedItem';

// helpers
import classnames from 'classnames';
import { getRecentlyViewedDisplayInfo } from '../helpers/recentlyViewedDisplayInfo';
import { trackPdpViewMoreCtaClick } from 'dibs-recent-history/src/recentHistoryTracking';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import { useAppSelector } from '../hooks/useReactReduxHooks';
import { trackTestimonialClick } from '../tracking/sharedCondensedTracking';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

// styles
import styles from './PdpRespCondensedContainer.scss';

export function PdpRespCondensedContainerComponent({
    currency,
    viewer,
    user,
    item,
    selectedSkuId,
    similarLandingItems,
}) {
    const ymalScrollRef = useRef(null);
    const { similarItemsGrid, similarLandingItemsGrid } = viewer;
    const { isTablet } = useServerVarsContext();
    const { isClient, isCondensedPdpRedesignVariant } = useAppSelector(
        ({ relayVariables }) => relayVariables.variables
    );
    const itemId = item?.serviceId;
    const isSellerAccessible = item?.seller?.isAccessible;
    const recentHistoryItems = (user?.recentHistoryItems?.edges || []).map(({ node }) => node);
    const { showRecentlyViewed } = getRecentlyViewedDisplayInfo({
        isClient,
        itemId,
        recentHistoryItems,
    });

    return (
        <>
            {isCondensedPdpRedesignVariant && (
                <div className={styles.listedContentWrapper}>
                    <div className={styles.listedContentContainer}>
                        <PdpRespCondensedListedContent
                            currency={currency}
                            item={item}
                            user={user}
                            viewer={viewer}
                            selectedSkuId={selectedSkuId}
                        />
                        {!isTablet && (
                            <PdpSharedTestimonial trackCtaClick={trackTestimonialClick} />
                        )}
                    </div>
                </div>
            )}
            {!isCondensedPdpRedesignVariant && (
                <>
                    <div className={styles.topBarWrapper}>
                        {isClient && (
                            <>
                                {showRecentlyViewed ? (
                                    <div className={styles.recentlyViewed}>
                                        <RecentlyViewedItemsLazy
                                            alignTitle="left"
                                            wrapperClassName={styles.recentlyViewedWrapper}
                                            itemsPerPage={6}
                                            authModalLoader={authModalLoader}
                                            trackViewMoreClick={trackPdpViewMoreCtaClick}
                                            currency={currency}
                                        />
                                    </div>
                                ) : (
                                    <PdpRespSimilarItemsLanding
                                        scrollToRef={ymalScrollRef}
                                        item={item}
                                        similarItems={similarLandingItems}
                                        viewer={viewer}
                                        user={user}
                                        landingItemSize={item?.isSold ? undefined : 'small'}
                                        currency={currency}
                                    />
                                )}
                            </>
                        )}
                    </div>

                    <PdpRespCondensedListedContent
                        currency={currency}
                        item={item}
                        user={user}
                        viewer={viewer}
                        selectedSkuId={selectedSkuId}
                    />
                </>
            )}

            {/* Similar or Recently Viewed Items */}
            {isCondensedPdpRedesignVariant && isClient && (
                <>
                    {showRecentlyViewed ? (
                        <RecentlyViewedItemsLazy
                            alignTitle="top"
                            centerTitle
                            wrapperClassName={styles.wrapperClassName}
                            carouselClassName={{
                                item: styles.carouselItemWrapper,
                                arrowLeft: styles.arrowLeft,
                                arrowRight: styles.arrowRight,
                                list: styles.list,
                            }}
                            itemsPerPage={5}
                            authModalLoader={authModalLoader}
                            trackViewMoreClick={trackPdpViewMoreCtaClick}
                            currency={currency}
                            size="large"
                        />
                    ) : (
                        <div className={styles.gridPlaceholder}>
                            <PdpRespSimilarItemsGrid
                                gridRowWrapperClass={styles.gridRowWrapperClass}
                                gridItemWrapperClass={styles.gridItemWrapperClass}
                                relayVariables={{ isClient }}
                                viewer={viewer}
                                currency={currency}
                                user={user}
                                item={item}
                                header={similarItems}
                                similarItems={similarLandingItemsGrid?.edges?.map(
                                    ({ node }) => node
                                )}
                                favoritesLocation={locations.SIMILAR_ITEM_GRID}
                                dataTn="pdp-similar-recommendation-grid"
                                isDark={false}
                                showLightBox={false}
                                tileType={SMALL_MEDIUM_TILE_TYPE}
                                hideBar
                                hideTileImagePadding
                                showMeasurements={false}
                            />
                        </div>
                    )}
                </>
            )}

            {/* More from Seller */}
            {isSellerAccessible && (
                <PdpRespSellerItemTileCarousel
                    user={user}
                    item={item}
                    viewer={viewer}
                    currency={currency}
                    isViewAllTopAligned
                />
            )}

            {/* Pdp Dealer Chat */}
            {isClient && <PdpRespDealerChatLoader viewer={viewer} currency={currency} />}

            {/* Circles */}
            <PdpRespCondensedCircles item={item} />

            {/* You May Also Like */}
            <div ref={ymalScrollRef}>
                <PdpRespSimilarItemsGrid
                    gridItemWrapperClass={styles.similarItems}
                    relayVariables={{ isClient }}
                    viewer={viewer}
                    currency={currency}
                    user={user}
                    item={item}
                    header={youMayAlsoLike}
                    similarItems={similarItemsGrid?.edges?.map(({ node }) => node)}
                    favoritesLocation={locations.SIMILAR_ITEM_GRID}
                    dataTn="pdp-similar-recommendation-grid"
                    isDark={false}
                    showLightBox={false}
                    hideBar
                />
            </div>

            {/* Recent History */}
            <PdpSharedRecentHistoryTrackerDeprecated item={item} />

            {/* Buyer Assurance Box */}
            <div className={classnames('primary-viewport-width', styles.buyerAssurance)}>
                <PdpRespBuyerAssuranceBox user={user} />
            </div>
        </>
    );
}

PdpRespCondensedContainerComponent.propTypes = {
    currency: PropTypes.string,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    selectedSkuId: PropTypes.string,
    similarLandingItems: PropTypes.object,
};

export const PdpRespCondensedContainer = createFragmentContainer(
    PdpRespCondensedContainerComponent,
    {
        viewer: graphql`
            fragment PdpRespCondensedContainer_viewer on Viewer {
                ...PdpRespSimilarItemsGrid_viewer
                ...PdpRespCondensedListedContent_viewer
                ...PdpRespSellerItemTileCarousel_viewer
                    @arguments(userCountryCode: $userCountryCode, priceBookName: $priceBookName)
                ...PdpRespDealerChatLoader_viewer @include(if: $isClient)

                similarLandingItemsGrid: moreLikeThisItem(
                    first: 5
                    itemId: $itemId
                    isTrade: $isTrade
                    onlyAuctionItems: true
                ) @include(if: $isCondensedPdpRedesignVariant) {
                    edges {
                        node {
                            serviceId
                            ...PdpRespSimilarItemsGrid_similarItems
                        }
                    }
                }

                similarItemsGrid: moreLikeThisItem(first: 16, seller: EXCLUDE, itemId: $itemId) {
                    edges {
                        node {
                            serviceId
                            ...PdpRespSimilarItemsGrid_similarItems
                        }
                    }
                }
            }
        `,
        user: graphql`
            fragment PdpRespCondensedContainer_user on User {
                serviceId
                ...PdpRespSimilarItemsGrid_user
                ...PdpRespSimilarItemsLanding_user
                ...PdpRespBuyerAssuranceBox_user
                ...PdpRespCondensedListedContent_user
                ...PdpRespSellerItemTileCarousel_user
                ...getRecentHistory_user @relay(mask: false)
            }
        `,
        item: graphql`
            fragment PdpRespCondensedContainer_item on Item {
                serviceId
                isSold
                seller {
                    isAccessible
                }
                ...PdpSharedRecentHistoryTrackerDeprecated_item
                ...PdpRespCondensedListedContent_item
                ...PdpRespSimilarItemsLanding_item
                ...PdpRespSellerItemTileCarousel_item
                ...PdpRespSimilarItemsGrid_item
                ...PdpRespCondensedCircles_item
            }
        `,
        similarLandingItems: graphql`
            fragment PdpRespCondensedContainer_similarLandingItems on moreLikeThisItemConnection {
                ...PdpRespSimilarItemsLanding_similarItems
            }
        `,
    }
);
